export interface IdleTrackerOptions {
  idleTime?: number;
  events?: string[];
  onIdle?: (() => void) | null;
  onActive?: (() => void) | null;
  onHide?: (() => void) | null;
  onShow?: (() => void) | null;
  keepTracking?: boolean;
  startAtIdle?: boolean;
  recurIdleCall?: boolean;
}

type TimerSet = (handler: TimerHandler, timeout?: number) => number;
type TimerClear = (id: number | undefined) => void;

const bulkAddEventListener = (
  target: EventTarget,
  events: string[],
  callback: EventListener,
): void => {
  events.forEach((event) => {
    target.addEventListener(event, callback);
  });
};

const bulkRemoveEventListener = (
  target: EventTarget,
  events: string[],
  callback: EventListener,
): void => {
  events.forEach((event) => {
    target.removeEventListener(event, callback);
  });
};

export class IdleTracker {
  private defaults: Required<IdleTrackerOptions> = {
    idleTime: 10000,
    events: ['mousemove', 'scroll', 'keydown', 'mousedown', 'touchstart'],
    onIdle: null,
    onActive: null,
    onHide: null,
    onShow: null,
    keepTracking: true,
    startAtIdle: false,
    recurIdleCall: false,
  };

  private settings: Required<IdleTrackerOptions>;
  private visibilityEvents: string[] = [
    'visibilitychange',
    'webkitvisibilitychange',
    'mozvisibilitychange',
    'msvisibilitychange',
  ];
  private clearTimeout: (() => void) | null = null;
  private lastActivityTime: Date = new Date();
  private visible: boolean = true;

  private idlenessEventsHandler: EventListener;
  private visibilityEventsHandler: EventListener;

  constructor(options: IdleTrackerOptions) {
    this.settings = { ...this.defaults, ...options };
    this.reset();

    this.idlenessEventsHandler = (_event: Event) => {
      if (this.isIdle) {
        this.setUserActive();
        if (this.settings.onActive) {
          this.settings.onActive.call(null);
        }
      } else {
        this.setUserActive();
      }

      this.resetTimeout(this.settings);
    };

    this.visibilityEventsHandler = (_event: Event) => {
      const hidden = document.hidden;
      //visibility changed
      if (hidden === this.visible) {
        this.visible = !hidden;
        const callback = this.visible
          ? this.settings.onShow
          : this.settings.onHide;
        if (callback) {
          callback.call(null);
        }
      }
    };
  }

  public get isIdle() {
    return !this.isActiveInLast(this.settings.idleTime);
  }

  public isActiveInLast(timeMs: number): boolean {
    const currentTime = new Date();
    const timeSinceLastActivity =
      currentTime.getTime() - this.lastActivityTime.getTime();
    return timeSinceLastActivity < timeMs;
  }

  private resetTimeout(
    settings: Required<IdleTrackerOptions>,
    keepTracking: boolean = this.settings.keepTracking,
  ): void {
    if (this.clearTimeout) {
      this.clearTimeout();
      this.clearTimeout = null;
    }
    if (keepTracking) {
      this.timeout(settings);
    }
  }

  private setUserActive(isActive = true): void {
    if (isActive) {
      this.lastActivityTime = new Date();
    } else {
      const past = new Date();
      past.setTime(0);
      this.lastActivityTime = past;
    }
  }

  private timeout(settings: Required<IdleTrackerOptions>): void {
    const timer: { set: TimerSet; clear: TimerClear } = settings.recurIdleCall
      ? {
          set: setInterval.bind(window) as TimerSet,
          clear: clearInterval.bind(window) as TimerClear,
        }
      : {
          set: setTimeout.bind(window) as TimerSet,
          clear: clearTimeout.bind(window) as TimerClear,
        };
    const id = timer.set(() => {
      if (this.settings.onIdle && this.isIdle) {
        this.settings.onIdle.call(null);
      }
    }, this.settings.idleTime);

    this.clearTimeout = () => timer.clear(id);
  }

  public start(): this {
    this.timeout(this.settings);
    bulkAddEventListener(
      window,
      this.settings.events,
      this.idlenessEventsHandler,
    );
    if (this.settings.onShow || this.settings.onHide) {
      bulkAddEventListener(
        document,
        this.visibilityEvents,
        this.visibilityEventsHandler,
      );
    }
    return this;
  }

  public stop(): this {
    bulkRemoveEventListener(
      window,
      this.settings.events,
      this.idlenessEventsHandler,
    );
    this.resetTimeout(this.settings, false);
    if (this.settings.onShow || this.settings.onHide) {
      bulkRemoveEventListener(
        document,
        this.visibilityEvents,
        this.visibilityEventsHandler,
      );
    }
    return this;
  }

  public reset({
    idle = this.settings.startAtIdle,
    visible = !this.settings.startAtIdle,
  } = {}): this {
    if (idle) {
      this.setUserActive(false);
    }
    this.visible = visible;
    return this;
  }
}
